import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarDays, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getDispatchWeeklyShifts, getEarliestDispatchShift } from '../../../../../services/staffingService';

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS = {
  null:      { label: 'Open',      bg: 'bg-red-50',      border: '#ef4444', text: 'text-red-700'   },
  open:      { label: 'Open',      bg: 'bg-red-50',      border: '#ef4444', text: 'text-red-700'   },
  accepted:  { label: 'Assigned',  bg: 'bg-sky-50',      border: '#0ea5e9', text: 'text-sky-700'   },
  active:    { label: 'Active',    bg: 'bg-blue-50',     border: '#3b82f6', text: 'text-blue-700'  },
  completed: { label: 'Completed', bg: 'bg-gray-50',     border: '#6b7280', text: 'text-gray-600'  },
  disputed:  { label: 'Disputed',  bg: 'bg-amber-50',    border: '#f59e0b', text: 'text-amber-700' },
  paid:      { label: 'Paid',      bg: 'bg-emerald-50',  border: '#10b981', text: 'text-emerald-700' },
};

const LEGEND = [
  { label: 'Open',      color: '#ef4444' },
  { label: 'Assigned',  color: '#0ea5e9' },
  { label: 'Active',    color: '#3b82f6' },
  { label: 'Completed', color: '#6b7280' },
  { label: 'Disputed',  color: '#f59e0b' },
  { label: 'Paid',      color: '#10b981' },
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Mon = 1
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function fmt(d, opts) {
  return new Date(d).toLocaleDateString('en-US', opts);
}

function fmtTime(d) {
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function monFirstIndex(jsDay) { return jsDay === 0 ? 6 : jsDay - 1; }

function toISO(d) { return new Date(d).toISOString().split('T')[0]; }

const ScheduleTab = () => {
  const { providerCompanyId } = useParams();
  const history = useHistory();

  const [calMode, setCalMode]   = useState('month'); // 'week' | 'month'
  const [monday, setMonday]     = useState(() => getMonday(new Date()));
  const [monthRef, setMonthRef] = useState(() => {
    const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [shifts, setShifts]     = useState([]);
  const [loading, setLoading]   = useState(true);

  // On mount: jump to the week of the earliest shift so the calendar isn't empty
  useEffect(() => {
    getEarliestDispatchShift(providerCompanyId)
      .then((resp) => {
        const date = resp.data.earliestShiftDate;
        if (date) {
          const d = new Date(date);
          setMonday(getMonday(d));
          setMonthRef({ year: d.getFullYear(), month: d.getMonth() });
        }
      })
      .catch(() => {}); // silent — default to current week
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerCompanyId]);

  // Determine fetch window based on mode
  const { fetchStart, fetchEnd } = calMode === 'week'
    ? { fetchStart: toISO(monday), fetchEnd: toISO(addDays(monday, 6)) }
    : (() => {
        const first = getFirstDayOfMonth(monthRef.year, monthRef.month);
        const last  = new Date(monthRef.year, monthRef.month + 1, 0);
        return { fetchStart: toISO(first), fetchEnd: toISO(last) };
      })();

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getDispatchWeeklyShifts(providerCompanyId, fetchStart, fetchEnd);
      setShifts(resp.data.shifts || []);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId, fetchStart, fetchEnd]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  // Build a map: dayIndex (0=Mon … 6=Sun) → shifts[]
  const dayShifts = Array.from({ length: 7 }, () => []);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  shifts.forEach((s) => {
    const d  = new Date(s.startTime);
    const diffDays = Math.round((d - monday) / 86400000);
    if (diffDays >= 0 && diffDays < 7) {
      dayShifts[diffDays].push(s);
    }
  });

  const handleShiftClick = (shift) => {
    // Always go to ShiftDetail — the command center for a single shift.
    // ShiftDetail shows all officers, lifecycle steps, and links to OfferDetail / JobDispatchDetail.
    history.push(`/provider-company/${providerCompanyId}/dispatch/shift/${shift._id}`);
  };

  // Navigation helpers
  const goPrev = () => {
    if (calMode === 'week') setMonday(d => addDays(d, -7));
    else setMonthRef(({ year, month }) => month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 });
  };
  const goNext = () => {
    if (calMode === 'week') setMonday(d => addDays(d, 7));
    else setMonthRef(({ year, month }) => month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 });
  };
  const goToday = () => {
    const n = new Date();
    setMonday(getMonday(n));
    setMonthRef({ year: n.getFullYear(), month: n.getMonth() });
  };

  // Labels
  const sunday    = addDays(monday, 6);
  const weekLabel = `${fmt(monday, { month: 'short', day: 'numeric' })} – ${fmt(sunday, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const monthLabel = `${MONTH_NAMES[monthRef.month]} ${monthRef.year}`;

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={goToday}
            className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
          >
            Today
          </button>
          <button
            onClick={goNext}
            className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted"
          >
            <ChevronRight size={16} />
          </button>
          <span className="ml-2 text-sm font-medium text-foreground">
            {calMode === 'week' ? weekLabel : monthLabel}
          </span>
        </div>

        {/* Week / Month toggle */}
        <div className="flex items-center gap-1 border-b border-transparent">
          {[{key:'week',label:'Week',Icon:CalendarDays},{key:'month',label:'Month',Icon:Calendar}].map(({key,label,Icon}) => (
            <button
              key={key}
              onClick={() => setCalMode(key)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                calMode === key
                  ? 'text-brand-blue-dark'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={13} /> {label}
              {calMode === key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-brand-blue-dark" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      {calMode === 'week' ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-border bg-muted/50">
            {DAY_NAMES.map((name, i) => {
              const day     = addDays(monday, i);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const isToday = day.toDateString() === today.toDateString();
              const dayShifts = Array.from({ length: 7 }, () => []);
              shifts.forEach((s) => {
                const d  = new Date(s.startTime);
                const diffDays = Math.round((d - monday) / 86400000);
                if (diffDays >= 0 && diffDays < 7) {
                  dayShifts[diffDays].push(s);
                }
              });
              const openCount = dayShifts[i].filter((s) => !s.offerStatus || s.offerStatus === 'open').length;
              return (
                <div
                  key={i}
                  className={`border-r border-border px-2 py-2.5 text-center last:border-r-0 ${isToday ? 'bg-brand-pink/5' : ''}`}
                >
                  <p className={`text-xs font-semibold ${isToday ? 'text-brand-pink' : 'text-muted-foreground'}`}>
                    {name} {fmt(day, { day: 'numeric' })}
                  </p>
                  <div className="mt-1 flex items-center justify-center gap-1">
                    <span className="text-[11px] text-muted-foreground">{dayShifts[i].length} shifts</span>
                    {openCount > 0 && (
                      <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        {openCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Shift slots */}
          {loading ? (
            <div className="grid grid-cols-7 divide-x divide-border">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="min-h-[200px] space-y-2 p-2">
                  {Array.from({ length: 2 }).map((_, j) => (
                    <div key={j} className="h-16 animate-pulse rounded-lg bg-muted" />
                  ))}
                </div>
              ))}
            </div>
          ) : (() => {
            const dayShifts = Array.from({ length: 7 }, () => []);
            shifts.forEach((s) => {
              const d  = new Date(s.startTime);
              const diffDays = Math.round((d - monday) / 86400000);
              if (diffDays >= 0 && diffDays < 7) {
                dayShifts[diffDays].push(s);
              }
            });
            return (
              <div className="grid grid-cols-7 divide-x divide-border">
                {dayShifts.map((daySlots, i) => (
                  <div key={i} className="min-h-[180px] space-y-1.5 p-1.5">
                    <AnimatePresence>
                      {daySlots.map((shift) => {
                        const cfg   = STATUS[shift.offerStatus] || STATUS.null;
                        return (
                          <motion.div
                            key={shift._id}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            onClick={() => handleShiftClick(shift)}
                            className={`cursor-pointer rounded-md p-1.5 ${cfg.bg} transition-all hover:-translate-y-0.5 hover:shadow-sm`}
                            style={{ borderLeft: `3px solid ${cfg.border}` }}
                          >
                            <p className="text-[11px] font-semibold text-card-foreground">
                              {fmtTime(shift.startTime)} – {fmtTime(shift.endTime)}
                            </p>
                            {/* Customer name — links to CRM profile */}
                            {shift.customerLocalId ? (
                              <p
                                className="truncate text-[11px] font-medium text-brand-blue-dark"
                              >
                                {shift.customerName}
                              </p>
                            ) : (
                              <p className="truncate text-[11px] text-muted-foreground">{shift.customerName}</p>
                            )}
                            {/* Service type */}
                            {shift.serviceType && (
                              <p className="truncate text-[10px] text-muted-foreground/70">{shift.serviceType}</p>
                            )}
                            {/* Fill badge: filledCount/staffsRequired */}
                            {shift.staffsRequired > 0 ? (
                              <span className={`mt-0.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-bold ${
                                shift.filledCount >= shift.staffsRequired
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : shift.filledCount > 0
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                              }`}>
                                {shift.filledCount ?? 0}/{shift.staffsRequired}
                              </span>
                            ) : null}
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                    {daySlots.length === 0 && (
                      <p className="mt-6 text-center text-[11px] text-muted-foreground/50">—</p>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          {/* Month view: day-of-week header */}
          <div className="grid grid-cols-7 border-b border-border bg-muted/50">
            {DAY_NAMES.map(name => (
              <div key={name} className="border-r border-border px-2 py-2 text-center last:border-r-0">
                <p className="text-xs font-semibold text-muted-foreground">{name}</p>
              </div>
            ))}
          </div>

          {/* Month grid */}
          {loading ? (
            <div className="grid grid-cols-7 divide-x divide-border">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="min-h-[90px] animate-pulse bg-muted/20" />
              ))}
            </div>
          ) : (() => {
            const today        = new Date(); today.setHours(0, 0, 0, 0);
            const firstDay     = getFirstDayOfMonth(monthRef.year, monthRef.month);
            const daysInMonth  = getDaysInMonth(monthRef.year, monthRef.month);
            const startPadding = monFirstIndex(firstDay.getDay());

            // Map ISO date string → shifts[]
            const byDate = {};
            shifts.forEach(s => {
              const iso = toISO(s.startTime);
              if (!byDate[iso]) byDate[iso] = [];
              byDate[iso].push(s);
            });

            // Build grid cells: padding + actual days
            const cells = [
              ...Array.from({ length: startPadding }, () => null),
              ...Array.from({ length: daysInMonth }, (_, i) => new Date(monthRef.year, monthRef.month, i + 1)),
            ];
            // Pad to full weeks
            while (cells.length % 7 !== 0) cells.push(null);

            return (
              <div className="grid grid-cols-7">
                {cells.map((day, i) => {
                  if (!day) return <div key={i} className="min-h-[90px] border-b border-r border-border bg-muted/10 last-of-type:border-r-0" />;
                  const iso        = toISO(day);
                  const daySlots   = (byDate[iso] || []).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
                  const isToday    = day.toDateString() === today.toDateString();
                  const isPast     = day < today;
                  const colIndex   = i % 7;
                  const openCount  = daySlots.filter(s => !s.offerStatus || s.offerStatus === 'open').length;
                  return (
                    <div
                      key={i}
                      className={`min-h-[90px] border-b border-r border-border p-1.5 ${colIndex === 6 ? 'border-r-0' : ''} ${isToday ? 'bg-brand-pink/5' : isPast ? 'bg-muted/10' : 'bg-card'}`}
                    >
                      <p className={`mb-1 text-right text-xs font-semibold ${isToday ? 'flex h-5 w-5 items-center justify-center rounded-full bg-brand-pink text-white ml-auto' : isPast ? 'text-muted-foreground/50' : 'text-foreground'}`}>
                        {day.getDate()}
                      </p>
                      <div className="space-y-0.5">
                        <AnimatePresence>
                          {daySlots.slice(0, 3).map(s => {
                            const cfg = STATUS[s.offerStatus] || STATUS.null;
                            return (
                              <motion.div
                                key={s._id}
                                initial={{ opacity: 0, y: 2 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                onClick={() => handleShiftClick(s)}
                                className={`cursor-pointer rounded-md p-1 ${cfg.bg} transition-all hover:-translate-y-0.5 hover:shadow-sm text-[10px] font-semibold text-card-foreground`}
                                style={{ borderLeft: `2px solid ${cfg.border}` }}
                              >
                                {fmtTime(s.startTime)}
                                {s.customerName && (
                                  <p className="truncate text-[9px] text-muted-foreground">{s.customerName}</p>
                                )}
                              </motion.div>
                            );
                          })}
                        </AnimatePresence>
                        {daySlots.length > 3 && (
                          <p className="text-[10px] text-muted-foreground pl-1">+{daySlots.length - 3} more</p>
                        )}
                        {openCount > 0 && (
                          <span className="inline-block rounded-full bg-red-500 px-1 py-0.5 text-[9px] font-bold text-white">{openCount} open</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Summary + Legend */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {LEGEND.map(({ label, color }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{shifts.length} shifts {calMode === 'week' ? 'this week' : 'this month'}</span>
          <span className="text-red-500 font-medium">
            {shifts.filter((s) => !s.offerStatus || s.offerStatus === 'open').length} open
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTab;
